import { useFlag } from '@unleash/proxy-client-react';

// generate this list by exporting feature flags as json from unleash
// copy the json, and paste it in this snippet: (<json goes here>).features.map(({ name }) => name)
// and just copy the resulting array
const FeatureFlagNames = [
  'accessToFormativeAssessment',
  'access_to_new_novelstudies',
  'add-material-file',
  'aiDrawingFeedback',
  'ai-templates',
  'AI_TEMPLATES',
  'AI_TEMPLATES_CONSUME',
  'browse_by_standards',
  'CLASSLINK_NEW_UNIQUE_ID',
  'CL_PAGE_V2',
  'CL_SLIDES_PREVIEW',
  'CURILIVE_FORCE_RELOAD',
  'CURIPOD_OUTTAKES',
  'custom-trial-expiration-dates',
  'discover',
  'discover_admin',
  'dynamic_curify',
  'dynamic_full_lesson',
  'dynamic_lesson_prompt_updates',
  'dynamic_lesson_v2',
  'enableNewCreatePage',
  'FALL_24_CONTENT_HIERARCHY',
  'GENERATOR_PAGE_RECOMMENDATIONS',
  'HOTJAR_MONITORING',
  'isAllowedToPublishGenerator',
  'isNewIAEnabled',
  'isTranslateTabEnabled',
  'IS_PRIVATE_SETTINGS',
  'IS_UNDER_MAINTENANCE',
  'isUseAttentionLock',
  'l10n-experiment',
  'localtest',
  'objective-refinement-on-landing',
  'orgPickerV2',
  'orgPickerV2districtSignupEnabled',
  'REVERSE_TRIAL_MVP',
  'rubric-based-ai-feedback',
  'SCHOOL_USAGE',
  'ADMIN_SCORE_INSIGHTS',
  'gaps-admin-dashboard',
  // The insights family names itself `insights_<audience>_<surface>` so a whole
  // audience's flags sort together in the Unleash list — you flip the two for
  // one audience as a set. The two admin flags above predate the scheme and
  // keep their legacy names; rename them to `insights_admin_dashboard` /
  // `insights_admin_gaps` when someone can coordinate the Unleash migration.
  //
  // Teacher-scoped insights dashboard: own classes + lessons, plus school and
  // by-grade aggregates. Sits beside ADMIN_SCORE_INSIGHTS, which wins when a
  // viewer holds both. Merges OFF; create in Unleash.
  'insights_teacher_dashboard',
  // The Gaps lens for teacher-scoped viewers — separate from the admin gaps
  // flag so gaps rolls out to admins and teachers on independent schedules.
  // Merges OFF; create in Unleash.
  'insights_teacher_gaps',
  // The Skill gaps lens (one row per student, and a student's every lesson),
  // layered on top of the role's gaps flag above. Merges OFF; create in Unleash.
  'skill_gaps_v2',
  'INTERNAL_ADMIN_DASHBOARD',
  'IN_PRODUCT_BACKOFFICE',
  'showMaintananceToast',
  'single_testprep_form',
  'STUDENT_FLEXIBILITY',
  'templated_customizable_lessons',
  'tenor_gifs',
  'useAiFeedbackPrompt',
  'useAzureOpenAi',
  'useCompareAndContrastLessonFlag',
  'useCountrySchoolSelectionV2',
  'useCurimoteNavigation',
  'useGeneratorPlugin',
  'use_new_dynamic_lesson_blocks',
  'useNewParticipantsLogic',
  'useNewReportPrompt',
  'useNewResults',
  'useOpenAIIsDown',
  'usePromptResultsTesting',
  'USE_RECOMMENDED_GENERATORS',
  'useTestingGeneratorsFlag',
  'show_individual_premium_unauthenticated',
  'international_individual_premium',
  'show_sample_lessons',
  'newCuriQuizFlow',
  'display_state_picker_home_internal',
  'activity-revision-editing',
  'vocabulary_dropdown',
  'auth-errors-troubleshoot',
  'lesson-limit-system-v2',
  'can_impersonate',
  'pre_made_math_lessons',
  'spelling_practice',
  'phonics_wizard',
  'LATIN_ROOTS_WIZARD',
  'SOCIAL_STUDIES_ROOTS_WIZARD',
  'SCIENCE_ROOTS_WIZARD',
  'grammar_practice',
  'drag_timer',
  'TESTPREP_RUBRIC_EDITING',
  'premium-testprep-play-limit',
  // DB standards for GA, SC, NC, OH, NJ, MI, LA, NV, WA, IA, CO, TN, NY, OK, MS — create in Unleash dashboard
  'use_db_standards_more_states',
  'internal_save_generated_lessons_as_free',
  'http_detect_strengths',
  'force_open_great_picks',
  'native_retry',
  'ai_feedback_score',
  'multiple_choice',
  'internal_copy_lesson_data',
  'as-many-as-you-can-competition',
  'org_members_can_edit_any_lesson',
  // Replaces the small "add a copy of this lesson" dialog on /edit with the
  // full-screen lesson preview (slides + copy/present CTAs). Merges OFF; flip
  // on in Unleash when ready.
  'lesson_preview_full_screen',
  // Dark-launch gate for the TX-only "How does Curipod fit my Curriculum?"
  // banner + curriculum-fit subpage on the writing home. Merges OFF; flip on
  // in Unleash when ready.
  'curriculum_fit_banner',
  // Swaps that banner for the "Practice That Aligns to Your Curriculum" card
  // section (one card per curriculum + Browse by TEKS) above Easy Ways to Get
  // Started. Only matters where curriculum_fit_banner already shows the
  // banner. Merges OFF; flip on in Unleash when ready.
  'curriculum_fit_home_cards',
  // Lesson preview modal on the curriculum-fit subpage: clicking a sample
  // lesson card opens the in-page slide preview (present/edit CTAs) instead of
  // navigating straight to /edit. Merges OFF; flip on in Unleash for testing.
  'curriculum_fit_lesson_preview',
  // Per-curriculum access on the curriculum-fit subpage. When on, that
  // curriculum's lessons are fully unlocked (no locks / paywall) and not-yet-
  // built units show "Coming soon" instead of the paywall.
  // Split by grade band: a district buys one vendor's programme, not the
  // vendor. The unsuffixed flags cover grades 3-5 (HMH Into Reading, Savvas
  // myView); the _6_8 flags cover the separate secondary programmes (Into
  // Literature, myPerspectives) that share the same dropdown row.
  'curriculum_access_bluebonnet',
  'curriculum_access_hmh',
  'curriculum_access_hmh_6_8',
  'curriculum_access_savvas',
  'curriculum_access_savvas_6_8',
  'math_word_problems',
  'STUDENT_SSO',
  'SSO_SELF_SERVE',
  // Internal debug switch, already in Unleash and read directly via `useFlag`
  // in the flexible-lesson levels. Also gates the agent's tool calls in the
  // refinement chat ("Looked up standards", "Read your file", …) — insight
  // into what the agent did, for us rather than for teachers.
  'flexible_lesson_debug',
  // Dark-launch gate for "Curipod skill plans" on the curriculum-fit page — the
  // goal-ordered third index (see skillPlansData.ts) alongside the curriculum
  // browsers and Browse by TEKS. Gates the dropdown row, the view, and the
  // deep link, so with it off the page behaves exactly as before. Sits inside
  // curriculum_fit_banner, which still gates the whole subpage. Merges OFF;
  // create in Unleash.
  'curriculum_skill_plans',
  // Dark-launch gate for the spreadsheet-style scope & sequence view of the
  // curriculum-fit page's folder browser. Teacher testing showed the card
  // columns don't read as a scope & sequence and don't carry enough per-lesson
  // information to decide what to click — the grid surfaces the skill, TEKS,
  // minutes and "where it fits" that curriculumBrowserData already holds.
  // Sits inside curriculum_fit_banner, which still gates the whole subpage;
  // with this off the browser renders exactly as before. Merges OFF; create in
  // Unleash.
  'curriculum_fit_spreadsheet_view',
  // Google Drive picker tabs: My Drive (folder view), Shared drives and Recent.
  // Off, the picker is the single flat file list it has always been. Shared
  // drive downloads need snapmentor/api#2357 deployed. Merges OFF; create in
  // Unleash.
  'google_drive_picker_tabs',
  // Shows the variant picker on the public generators page
  // (/generators/[seoTitle]/g/[generatorId]) without the rest of AI_TEMPLATES.
  // AI_TEMPLATES still grants the picker on its own. Merges OFF; create in
  // Unleash.
  'generators_page_variant_access',
  // AI feedback rubrics stored in the database (FOF-3): lists them in Edit AI
  // Feedback's "Choose a Rubric" and, with AI_TEMPLATES, shows the Rubrics tab.
  // Off, both behave exactly as before. Merges OFF; create in Unleash.
  'RUBRICS',
  // "Read & reflect" host flow for AI feedback activities that end on the
  // scoreboard: word count → scoreboard → "Read the feedback on your device"
  // overlay → all responses grid → the top 3 picks (unchanged) → close.
  // Off, the scoreboard flow behaves exactly as before. Merges OFF; create in
  // Unleash.
  'read_reflect_feedback',
  // Host view for voting (FOF-33): while students vote, the host screen shows
  // a black screen with the voting title centered (the "Read the feedback"
  // overlay) instead of the slide. It also replaces the "Ready / Set" rings
  // before EVERY activity round (not only voting) with a pure-black 3 · 2 · 1
  // countdown that fades into the activity; the 3.7 s start delay is
  // unchanged. Off, all of this looks exactly as before.
  // Merges OFF; create in Unleash.
  'host_voting_title_screen',
  // Native Spanish narrators (Joaquin, then Maria) appended at the bottom of
  // the teacher read-aloud voice picker. Off, the list, its order, and the
  // default voice are unchanged. Merges OFF; create in Unleash.
  'read_aloud_spanish_voices',
  // The new lesson report (/reports/{gameId}): whether each student met the
  // lesson's learning objective, classified by the api, replaces the old
  // Summary / Students / Activities report for the host. Also the Reports
  // tabs, with Student profiles of the teacher's own students (no Growth).
  // Experiment. Merges OFF; create in Unleash.
  'lesson_objective_report',
  // The lesson report for admins: everything the flag above gives, plus the
  // Growth tab and Student profiles with the school and teacher pickers. With
  // either flag on, these two alone decide what the Reports tabs show
  // (useInsightsAccess). Merges OFF; create in Unleash.
  'lesson_objective_report_admin',
] as const;

type FeatureFlagName = typeof FeatureFlagNames[number];

export function useFeatureFlag(name: FeatureFlagName): boolean {
  const isEnabled = useFlag(name);
  return isEnabled;
}
